/* eslint-disable react/jsx-no-bind */
import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FC, useCallback, useMemo } from "react";

export type DistributionGroupOption = {
  id: string;
  label: string;
  funnelName: string;
};

type Props = {
  popupGroupOptions: DistributionGroupOption[];
  abtestGroupOptions: DistributionGroupOption[];
  selectedPopupGroupIds: string[];
  selectedAbtestGroupIds: string[];
  onPopupGroupIdsChange: (ids: string[]) => void;
  onAbtestGroupIdsChange: (ids: string[]) => void;
  disabled?: boolean;
};

type GroupedOptions = {
  funnelName: string;
  options: DistributionGroupOption[];
};

const groupByFunnel = (
  options: DistributionGroupOption[]
): GroupedOptions[] => {
  const map = new Map<string, DistributionGroupOption[]>();
  options.forEach((option) => {
    const key = option.funnelName || "（ファネル未設定）";
    const existing = map.get(key) ?? [];
    map.set(key, [...existing, option]);
  });
  return Array.from(map.entries()).map(([funnelName, grouped]) => ({
    funnelName,
    options: grouped,
  }));
};

const sectionBoxProps = {
  border: "1px solid",
  borderColor: "gray.200",
  borderRadius: "md",
  p: 4,
} as const;

const CheckboxSection: FC<{
  title: string;
  prefix: string;
  options: DistributionGroupOption[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}> = ({ title, prefix, options, selectedIds, onChange, disabled }) => {
  const grouped = useMemo(() => groupByFunnel(options), [options]);

  const handleToggle = useCallback(
    (id: string, checked: boolean) => {
      if (checked) {
        onChange([...selectedIds, id]);
        return;
      }
      onChange(selectedIds.filter((value) => value !== id));
    },
    [onChange, selectedIds]
  );

  const selectedChips = useMemo(
    () => options.filter((option) => selectedIds.includes(option.id)),
    [options, selectedIds]
  );

  if (options.length === 0) {
    return (
      <Box {...sectionBoxProps}>
        <FormControl>
          <FormLabel fontSize="sm" mb={0}>
            {title}
          </FormLabel>
          <Text fontSize="sm" color="gray.500" mt={2}>
            選択できる{title}がありません
          </Text>
        </FormControl>
      </Box>
    );
  }

  return (
    <Box {...sectionBoxProps}>
      <FormControl>
        <FormLabel fontSize="sm" mb={0}>
          {title}
        </FormLabel>
        {selectedChips.length > 0 && (
          <Wrap spacing={2} mb={3} mt={2}>
            {selectedChips.map((option) => (
              <WrapItem key={option.id}>
                <Tag size="sm" colorScheme="blue" borderRadius="full">
                  <TagLabel>
                    {prefix}: {option.label}
                  </TagLabel>
                  <TagCloseButton
                    isDisabled={disabled}
                    onClick={() => handleToggle(option.id, false)}
                  />
                </Tag>
              </WrapItem>
            ))}
          </Wrap>
        )}
        <VStack
          align="stretch"
          spacing={3}
          maxH="200px"
          overflowY="auto"
          mt={2}
        >
          {grouped.map((group) => (
            <Box key={group.funnelName}>
              <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                {group.funnelName}
              </Text>
              <VStack align="stretch" spacing={1}>
                {group.options.map((option) => (
                  <Checkbox
                    key={option.id}
                    isChecked={selectedIds.includes(option.id)}
                    isDisabled={disabled}
                    onChange={(e) => handleToggle(option.id, e.target.checked)}
                  >
                    <HStack spacing={2}>
                      <Text fontSize="sm">{option.label}</Text>
                    </HStack>
                  </Checkbox>
                ))}
              </VStack>
            </Box>
          ))}
        </VStack>
      </FormControl>
    </Box>
  );
};

export const UserTagModuleLinkFields: FC<Props> = ({
  popupGroupOptions,
  abtestGroupOptions,
  selectedPopupGroupIds,
  selectedAbtestGroupIds,
  onPopupGroupIdsChange,
  onAbtestGroupIdsChange,
  disabled = false,
}) => {
  return (
    <VStack align="stretch" spacing={4}>
      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
        紐付け先（任意）
      </Text>
      <Text fontSize="sm" color="gray.600" lineHeight="tall">
        ポップアップまたはABテストグループをリンクすると、リンク先の配信条件（期間設定・配信セグメント・配信設定など）をそのまま継承します。複数リンク時はいずれかの条件を満たせば実行されます（OR）。
        ポップアップのみ：再表示禁止期間、要素の表示/非表示条件も継承されます。未リンクのモジュールは従来どおり常に実行されます。
      </Text>
      <CheckboxSection
        title="ポップアップグループ"
        prefix="ポップアップ"
        options={popupGroupOptions}
        selectedIds={selectedPopupGroupIds}
        onChange={onPopupGroupIdsChange}
        disabled={disabled}
      />
      <CheckboxSection
        title="ABテストグループ"
        prefix="AB"
        options={abtestGroupOptions}
        selectedIds={selectedAbtestGroupIds}
        onChange={onAbtestGroupIdsChange}
        disabled={disabled}
      />
    </VStack>
  );
};
